export const CHANNEL_TYPES = ['telegram', 'whatsapp', 'imessage', 'slack', 'email'] as const; // widens per slice
export type ChannelType = (typeof CHANNEL_TYPES)[number];

export function isChannelType(v: unknown): v is ChannelType {
  return typeof v === 'string' && (CHANNEL_TYPES as readonly string[]).includes(v);
}

/**
 * A message that arrived in a GROUP chat (spec 2026-10-05 groups §4.1): a Telegram group or supergroup, an iMessage group
 * space. Read from the platform's own structured fields by the adapter, never from the text. A group is a room, never a
 * DM: the gateway must not run a personal turn on it.
 */
export interface InboundGroup {
  /** The message names Bash: a Telegram `mention` / `text_mention` / `bot_command@bot` entity for this bot. */
  mentionsBash: boolean;
  /** The message replies to one of Bash's own messages. */
  replyToBash: boolean;
  /** The group's own name, as the platform gives it (Telegram `chat.title`). Absent when it gave none. */
  title?: string;
  /**
   * What the sender is called, as the platform gives it (Telegram `from.first_name`, else `from.username`): the window's
   * label for their lines and the room's name for its owner. Absent when it gave none.
   */
  senderName?: string;
}

/** A normalized inbound message from any channel adapter. */
export interface InboundMessage {
  channel: ChannelType;
  externalUserId: string; // stable per-channel sender id (Telegram numeric id)
  externalChatId: string; // the conversation id to reply into
  text: string;
  raw: unknown; // original platform payload, for debugging
  /**
   * Platform id that is STABLE across a redelivery of this same message
   * (Telegram `update_id`, Twilio `MessageSid`), namespaced per channel:
   * `tg:1234`, `wa:SM…`. The adapter mints it because only the adapter knows
   * the payload shape — the ingress path must never cast `raw` to find it.
   * Absent when a channel has no such id.
   */
  dedupeKey?: string;
  /**
   * The vendor's id for THIS message, the thing a reaction is put on: the
   * WhatsApp wamid, the Telegram message_id (not the update_id the dedupe key
   * carries), the iMessage guid. Minted by the adapter for the same reason the
   * dedupe key is. Absent when the vendor gave none.
   */
  messageId?: string;
  /**
   * Phase 6 (spec §3.4): a reaction the person put on one of OUR messages.
   * `targetMessageId` is the vendor id of the message it landed on (the wamid
   * we were handed on send, the Telegram message_id, the iMessage guid) and
   * `emoji` is what they put. A message carrying this has `text: ''` and the
   * controller routes it before any turn. Minted by the adapter for the same
   * reason `messageId` is. A REMOVED reaction is never minted.
   */
  reaction?: { targetMessageId: string; emoji: string };
  /**
   * Files the sender attached, as vendor handles — NOT bytes. The gateway
   * fetches and ingests them; the adapter only names them. Absent when the
   * message carried none, or when the adapter cannot fetch this channel yet.
   */
  media?: InboundMediaRef[];
  /**
   * Slack rooms (spec 2026-09-28 §7.2): present, and false, only on an owner's message in a channel thread Bash has
   * posted in that did NOT mention Bash. The turn is told it was not named and may stay silent. Absent everywhere
   * else, so every other channel's message is the message it always was.
   */
  addressed?: false;
  /**
   * Present only on a GROUP chat's message (spec 2026-10-05 groups §4.1). Absent on a direct chat, so every direct
   * message is the message it always was.
   */
  group?: InboundGroup;
}

/**
 * Vendor media arms an adapter may fetch AND the composer can use.
 *
 * AUDIO IS IN, on the condition this comment used to name: "audio joins this
 * set when transcription exists, and not before." It exists now, so the arm is
 * claimed rather than apologised for — and the swallowing this set was written
 * to prevent is answered by {@link MediaKind} instead, which every vendor
 * supplies from its own payload. A song lands in the user's files like any
 * other upload; a voice note additionally becomes the turn's words.
 */
export const FETCHABLE_MEDIA: ReadonlySet<string> = new Set([
  'image',
  'video',
  'document',
  'sticker',
  'audio',
]);

export function isFetchableMedia(arm: string | undefined): boolean {
  return typeof arm === 'string' && FETCHABLE_MEDIA.has(arm);
}

/**
 * A voice note is someone TALKING; an audio file is a thing to work on. Every
 * vendor draws this line itself, so we never have to guess from the bytes.
 *
 * The two destinies differ: a 'file' is stored and named, a 'voice' is stored
 * AND transcribed, and its transcript becomes the turn's words.
 */
export type MediaKind = 'file' | 'voice';

/**
 * The closed set a composer reaction may draw from. It is exactly the iMessage
 * Tapback set, which is why it is closed: every chat channel we serve can put
 * one of these six on a message, and each adapter maps a key to its own glyph.
 * The composer speaks keys, never glyphs, so a channel that renders "emphasize"
 * as ‼️ and one that renders it as 🔥 both stay honest to the same intent.
 */
export const REACTION_KEYS = ['like', 'love', 'laugh', 'emphasize', 'question', 'dislike'] as const;
export type ReactionKey = (typeof REACTION_KEYS)[number];

export function isReactionKey(v: unknown): v is ReactionKey {
  return typeof v === 'string' && (REACTION_KEYS as readonly string[]).includes(v);
}

/** A vendor-side handle for one inbound file. Opaque outside its adapter. */
export interface InboundMediaRef {
  /** The vendor's id or URL for these bytes. */
  handle: string;
  /**
   * What the sender MEANT by this upload, read off the vendor's own payload.
   *
   * REQUIRED, deliberately. An optional field would default to 'file' at every
   * site that forgot it, and the failure that produces is a spoken request
   * filed away as an attachment nobody reads — silent, and exactly the bug this
   * exists to end. A ref with no classification is a bug, so the type says so.
   */
  kind: MediaKind;
  /** Vendor-declared MIME, when it declares one. */
  mediaType?: string;
  /** Vendor-declared filename, when it declares one. */
  filename?: string;
  /** Vendor-declared size, when it declares one — lets us refuse before fetching. */
  sizeBytes?: number;
}

/** An image small enough to show the model, in the provider's base64 shape. */
export interface VisionImage {
  data: string;
  mediaType: 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp';
}

/** Maps a channel identity to a Vestra user. (Chatwoot source_id model.) */
export interface ChannelLink {
  id: string;
  userId: string;
  channel: ChannelType;
  externalId: string;
  displayName?: string | null;
  status: 'active' | 'revoked';
  createdAt: string;
}

/** Maps a channel conversation to its current Task. */
export interface ChannelConversation {
  id: string;
  channel: ChannelType;
  externalChatId: string;
  userId: string;
  currentTaskId: string | null;
  createdAt: string;
  updatedAt: string;
}
